import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";
import dfSubDays from "date-fns/subDays";

import { formatDate, getDateByTimeZone } from "@/lib/date";
import { Delivery } from "@/Models";
import { useApiStore } from "@/store/hooks";
import {
  QUERY_DATE_TYPE_RECEIPT_DATE,
  QUERY_DELIVERY_TYPE_ALL,
  QUERY_DELIVERY_TYPE_CANCELED,
  QUERY_DELIVERY_TYPE_DELIVERY_LOST,
  QUERY_DELIVERY_TYPE_DELIVERY_SENT_BACK,
} from "@/store/Deliveries";

export const CONSTANTS = {
  FETCH_ALL: "FETCH_ALL",
  SET_QUERY: "SET_QUERY",
};

export const CASE_ALL = "ALL";
export const CASE_SENT_BACK = "SENT_BACK";
export const CASE_LOST = "LOST";
export const CASE_CANCELED = "CANCELED";
export const CASE_NOT_PRINTED = "NOT_PRINTED";
export const HANJIN_RETURN_CORP_USER_NAME =
  process.env.REACT_APP_ENV === "production" ? "한진국내" : "한진국내당일배송";

const CASE_QUERY_TYPES = {
  [CASE_ALL]: [QUERY_DELIVERY_TYPE_ALL],
  [CASE_SENT_BACK]: [QUERY_DELIVERY_TYPE_DELIVERY_SENT_BACK],
  [CASE_LOST]: [QUERY_DELIVERY_TYPE_DELIVERY_LOST],
  [CASE_CANCELED]: [QUERY_DELIVERY_TYPE_CANCELED],
  [CASE_NOT_PRINTED]: [QUERY_DELIVERY_TYPE_ALL],
};

export const getInitialQuery = () => {
  const zonedDateToday = getDateByTimeZone();
  const zonedDateFrom = dfSubDays(zonedDateToday, 10);

  return {
    page: 1,
    pageSize: 20,
    dateType: QUERY_DATE_TYPE_RECEIPT_DATE,
    dateFrom: formatDate(zonedDateFrom),
    dateTo: formatDate(zonedDateToday),
    bookId: "",
    orderIdFromCorp: "",
    orderIdFromCorpSub: "",
    caseFilter: CASE_ALL,
  };
};

const INITIAL_STATE = {
  deliveries: [],
  pageCount: 1,
  query: getInitialQuery(),
};

const reducer = (state, action) => {
  switch (action.type) {
    case CONSTANTS.FETCH_ALL:
      return {
        ...state,
        deliveries: action.deliveries,
        pageCount: action.pageCount,
      };
    case CONSTANTS.SET_QUERY:
      return {
        ...state,
        query: {
          ...state.query,
          ...action.query,
        },
      };
    default:
      return state;
  }
};

export const Context = createContext(INITIAL_STATE);

export const Provider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  return (
    <Context.Provider value={{ dispatch, state }}>{children}</Context.Provider>
  );
};

const buildQuery = (stateQuery, { forNotPrinted = false } = {}) => {
  const caseFilter = forNotPrinted
    ? CASE_NOT_PRINTED
    : stateQuery.caseFilter || CASE_ALL;
  const queryTypes = CASE_QUERY_TYPES[caseFilter] || [QUERY_DELIVERY_TYPE_ALL];

  const query = {
    page: forNotPrinted ? 1 : stateQuery.page,
    pageSize: forNotPrinted ? 100 : stateQuery.pageSize,
    dateType: QUERY_DATE_TYPE_RECEIPT_DATE,
    dateFrom: stateQuery.dateFrom,
    dateTo: stateQuery.dateTo,
    types: "3",
    corpUserName: HANJIN_RETURN_CORP_USER_NAME,
    queryTypes: queryTypes.includes(QUERY_DELIVERY_TYPE_ALL)
      ? ""
      : queryTypes.join(";"),
  };

  if (stateQuery.bookId) {
    query.bookId = stateQuery.bookId;
  }

  if (stateQuery.orderIdFromCorp) {
    query.orderIdFromCorp = stateQuery.orderIdFromCorp;
  }

  if (stateQuery.orderIdFromCorpSub) {
    query.orderIdFromCorpSub = stateQuery.orderIdFromCorpSub;
  }

  if (forNotPrinted || caseFilter === CASE_NOT_PRINTED) {
    query.printed = false;
  }

  return query;
};

export const useHanjinReturnWaybillsStore = () => {
  const api = useApiStore();
  const { dispatch, state } = useContext(Context);

  const fetchAll = useCallback(async () => {
    const query = buildQuery(state.query);
    const { deliveries = [], pageCount = 1 } = await api.get(
      "/deliveries/by-rider-group-managers",
      query,
    );

    dispatch({
      type: CONSTANTS.FETCH_ALL,
      deliveries: deliveries.map((d) => new Delivery(d)),
      pageCount,
    });
  }, [api.get, dispatch, state.query]);

  const fetchNotPrinted = useCallback(async () => {
    const query = buildQuery(state.query, { forNotPrinted: true });
    const { deliveries = [] } = await api.get(
      "/deliveries/by-rider-group-managers",
      query,
    );

    return deliveries.map((d) => new Delivery(d));
  }, [api.get, state.query]);

  const setQuery = useCallback(
    (query = {}) => {
      dispatch({
        type: CONSTANTS.SET_QUERY,
        query,
      });
    },
    [dispatch],
  );

  const resetQuery = useCallback(() => {
    const query = getInitialQuery();

    dispatch({
      type: CONSTANTS.SET_QUERY,
      query,
    });

    return query;
  }, [dispatch]);

  return {
    state,
    fetchAll,
    fetchNotPrinted,
    setQuery,
    resetQuery,
  };
};
